import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { useAuth } from './contexts/AuthContext';
import { antdTheme } from './theme/antd-theme';
import Login from './pages/Login';
import StatusPage from './pages/Status';
import Dashboard from './pages/Dashboard';
import Tickets from './pages/Tickets';
import TicketDetail from './pages/TicketDetail';
import AgentWorkspace from './pages/AgentWorkspace';
import Businesses from './pages/Businesses';
import BusinessDetail from './pages/BusinessDetail';
import CreateBusiness from './pages/CreateBusiness';
import Users from './pages/Users';
import Equipment from './pages/Equipment';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import ResetPassword from './pages/ResetPassword';
import RequestAccount from './pages/RequestAccount';
import Calls from './pages/Calls';
import RemoteAccess from './pages/RemoteAccess';
import AgentCallCenter from './pages/AgentCallCenter';
import Feedback from './pages/Feedback';
import FeedbackThankYou from './pages/FeedbackThankYou';
import FeedbackDashboard from './pages/FeedbackDashboard';
import TVDashboard from './pages/TVDashboard';
import SalesPortal from './pages/SalesPortal';
import SalesPortalAdmin from './components/SalesPortalAdmin';
import AuditLogs from './pages/AuditLogs';
import SearchResults from './pages/SearchResults';
import Chat from './pages/Chat';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';

function App() {
  const { user, loading } = useAuth();

  // Set default page title
  useEffect(() => {
    document.title = 'Vitreous Support - Support Ticket Platform';
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return (
      <ConfigProvider theme={antdTheme}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/request-account" element={<RequestAccount />} />
          <Route path="/feedback/:token" element={<Feedback />} />
          <Route path="/feedback/thank-you" element={<FeedbackThankYou />} />
          <Route path="/tvdashboard" element={<TVDashboard />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={antdTheme}>
      <Routes>
        {/* TV Dashboard - No Layout/Sidebar */}
        <Route path="/tvdashboard" element={<TVDashboard />} />
        <Route path="/status" element={<StatusPage />} />
        
        {/* All other routes with Layout */}
        <Route path="*" element={
          <Layout>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/tickets" element={<Tickets />} />
              <Route path="/tickets/:id" element={<TicketDetail />} />
              <Route path="/workspace/number/:ticketNumber" element={<AgentWorkspace />} />
              <Route path="/workspace/:id" element={<AgentWorkspace />} />
              <Route path="/businesses" element={<Businesses />} />
              <Route path="/businesses/new" element={<CreateBusiness />} />
              <Route path="/businesses/:id/edit" element={<CreateBusiness />} />
              <Route path="/businesses/:id" element={<BusinessDetail />} />
              {user.role === 'admin' && (
                <>
                  <Route path="/users" element={<Users />} />
                  <Route path="/equipment" element={<Equipment />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/feedback-dashboard" element={<FeedbackDashboard />} />
                  <Route path="/audit-logs" element={<AuditLogs />} />
                </>
              )}
              <Route path="/profile" element={<Profile />} />
              <Route path="/search" element={<SearchResults />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/call-center" element={<AgentCallCenter />} />
              <Route path="/calls" element={<Calls />} />
              <Route path="/remote" element={<RemoteAccess />} />
              {user.role === 'admin' && (
                <Route path="/salesportal" element={<SalesPortalAdmin />} />
              )}
              {(user.role === 'sales' || user.role === 'sales_agent' || user.role === 'sales_rep_internal') && (
                <Route path="/salesportal" element={<SalesPortal />} />
              )}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Layout>
        } />
      </Routes>
    </ConfigProvider>
  );
}

export default App;
